import React, { useState, useEffect, useRef, useCallback } from 'react';
import PublicationCard from '@/features/publication/components/PublicationCard';
import { getData, postData } from '@/features/shared/lib/api';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

export default function Publications() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all'); // 'all', 'confirmed', 'unconfirmed'
  const [totalPapers, setTotalPapers] = useState(0);
  const [confirmedPapers, setConfirmedPapers] = useState(0);
  const [unconfirmedPapers, setUnconfirmedPapers] = useState(0);

  // 모달 상태
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const [editCategory, setEditCategory] = useState('기타'); // '내 논문이 아닙니다.', '저자 정보가 틀렸습니다.', '학과 정보가 틀렸습니다.', '기타'
  const [editContent, setEditContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 성공 팝업 상태
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const isFirstLoadRef = useRef(true);
  const userId = typeof rmVars !== 'undefined' && rmVars?.scholar_id ? rmVars.scholar_id : null;

  // fetchList 함수
  const fetchList = useCallback(async () => {
    if (!userId) return;

    try {
      if (isFirstLoadRef.current) {
        setLoading(true);
      }
      setErr(null);
      const res = await getData('/rm/v1/my_library/resources', {
        current_page: page,
        record_per_page: 5,
        ks: '',
        sub_ks: '',
        order_by: 'date',
        order: 'desc',
        my_library: true,
      });

      if (res?.records) {
        setRows(res.records);

        // 전체/확인된/미확인 논문 수 계산
        const all = res.records.length;
        const confirmed = res.records.filter(r => r.self_confirm === 'Y' || r.self_confirm === true).length;
        const unconfirmed = all - confirmed;

        setTotalPapers(all);
        setConfirmedPapers(confirmed);
        setUnconfirmedPapers(unconfirmed);
      }
    } catch (e) {
      setErr(e?.response?.data?.message || e.message);
    } finally {
      if (isFirstLoadRef.current) {
        setLoading(false);
        isFirstLoadRef.current = false;
      }
    }
  }, [userId, page]);

  useEffect(() => {
    let mounted = true;
    fetchList();
    return () => { mounted = false; };
  }, [fetchList]);

  // 필터링된 리스트
  const filteredRows = rows.filter((item) => {
    if (filter === 'confirmed') {
      return item.self_confirm === 'Y' || item.self_confirm === true;
    } else if (filter === 'unconfirmed') {
      return item.self_confirm !== 'Y' && item.self_confirm !== true;
    }
    return true; // 'all'
  });

  // "예" 버튼 클릭 핸들러
  const handleConfirm = async (recordId) => {
    try {
      setSubmitting(true);
      const res = await postData('/rm/v1/my_library/resource', {
        record_id: String(recordId),
        self_confirm: 'Y',
      });

      if (res?.success === true) {
        // 로컬 상태 즉시 업데이트 (UI 반영을 위해)
        setRows(prevRows =>
          prevRows.map(item => {
            const id = item.record_id || item.resource_id || item.id;
            if (String(id) === String(recordId)) {
              return { ...item, self_confirm: 'Y' };
            }
            return item;
          })
        );

        // 통계 업데이트
        setConfirmedPapers(prev => prev + 1);
        setUnconfirmedPapers(prev => Math.max(0, prev - 1));

        setSuccessMessage('감사합니다');
        setSuccessModalOpen(true);
        // 데이터 다시 불러오기
        await fetchList();
      }
    } catch (e) {
      alert('처리 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  // "아니요" 버튼 클릭 핸들러
  const handleReject = (recordId) => {
    setCurrentRecordId(recordId);
    setEditCategory('기타');
    setEditContent('');
    setEditModalOpen(true);
  };

  // 수정 요청 팝업 저장 핸들러
  const handleEditSubmit = async () => {
    if (!currentRecordId) return;

    // category 매핑
    const categoryMap = {
      '내 논문이 아닙니다.': '내 논문이 아닙니다.',
      '저자 정보가 틀렸습니다.': '저자 정보가 틀렸습니다.',
      '학과 정보가 틀렸습니다.': '학과 정보가 틀렸습니다.',
      '기타': '내 논문 문의',
    };

    const category = categoryMap[editCategory] || '내 논문 문의';

    if (!editContent.trim()) {
      alert('문의 내용을 작성해주세요.');
      return;
    }

    try {
      setSubmitting(true);
      const res = await postData('/rm/v1/my_library/resource', {
        category: category,
        content: editContent.trim(),
        kboard_option_resource_id: String(currentRecordId),
        record_id: String(currentRecordId),
        self_confirm: 'N',
      });

      if (res?.success === true) {
        setEditModalOpen(false);
        setSuccessMessage('수정 요청되었습니다');
        setSuccessModalOpen(true);
        // 데이터 다시 불러오기
        await fetchList();
      }
    } catch (e) {
      alert('처리 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center w-full">
        <h3 className="text-[26px] lg:text-[24px] font-black lg:font-bold leading-[130%] lg:leading-[100%]">Publications</h3>
      </div>

      <div className="mt-[16px] lg:mt-6 lg:p-[60px] bg-white rounded-[12px]">
        <h3 className="mb-4 lg:mb-6 text-[18px] font-bold leading-[100%]">논문 확인 현황</h3>

        {/* 현황 */}
        <div className="flex justify-between mb-6 lg:mb-8 py-4 lg:py-[30px] text-center bg-[#F3F5F9] rounded-[12px]">
          <div className="flex flex-col lg:flex-col-reverse gap-[10px] lg:gap-2 w-[32%]">
            <p className="text-[13px] font-bold text-[#6C7687] leading-[120%]">
              전체 <br className="lg:hidden" />논문수
            </p>
            <p className="text-[20px] font-black">{totalPapers}</p>
          </div>

          <div className="w-[1px] bg-[#E4E8F1]"></div>

          <div className="flex flex-col lg:flex-col-reverse gap-[10px] lg:gap-2 w-[32%]">
            <p className="text-[13px] font-bold text-[#6C7687] leading-[120%]">
              저자가 확인한 <br className="lg:hidden" />논문수
            </p>
            <p className="text-[20px] text-[#4B4DFF] font-black">{confirmedPapers}</p>
          </div>

          <div className="w-[1px] bg-[#E4E8F1]"></div>

          <div className="flex flex-col lg:flex-col-reverse gap-[10px] lg:gap-2 w-[32%]">
            <p className="text-[13px] font-bold text-[#6C7687] leading-[120%]">
              미확인 <br className="lg:hidden" />논문수
            </p>
            <p className="text-[20px] text-[#F3652D] font-black">{unconfirmedPapers}</p>
          </div>
        </div>

        {/* 전체/확인된/미확인 구분 */}
        <div className="flex gap-[22px] mb-6 lg:mb-8">
          <label className="form-wrap group inline-flex cursor-pointer select-none">
            <input
              type="radio"
              name="publica-Filter"
              className="peer hidden"
              checked={filter === 'all'}
              onChange={() => setFilter('all')}
            />
            <span className="w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-full group-hover:border-[#4B4DFF] peer-checked:border-[4px] peer-checked:border-[#4B4DFF] peer-checked:bg-[#fff] transition-colors duration-200 translate-y-[3px]">
              <span className="w-2 h-2 rounded-full bg-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200"></span>
            </span>
            <span className="ml-[10px] text-[14px] text-[#6C7687] translate-y-[1px]">전체 논문</span>
          </label>

          <label className="form-wrap group inline-flex cursor-pointer select-none">
            <input
              type="radio"
              name="publica-Filter"
              className="peer hidden"
              checked={filter === 'confirmed'}
              onChange={() => setFilter('confirmed')}
            />
            <span className="w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-full group-hover:border-[#4B4DFF] peer-checked:border-[4px] peer-checked:border-[#4B4DFF] peer-checked:bg-[#fff] transition-colors duration-200 translate-y-[3px]">
              <span className="w-2 h-2 rounded-full bg-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200"></span>
            </span>
            <span className="ml-[10px] text-[14px] text-[#6C7687] translate-y-[1px]">확인된 논문</span>
          </label>

          <label className="form-wrap group inline-flex cursor-pointer select-none">
            <input
              type="radio"
              name="publica-Filter"
              className="peer hidden"
              checked={filter === 'unconfirmed'}
              onChange={() => setFilter('unconfirmed')}
            />
            <span className="w-4 h-4 flex items-center justify-center border border-[#B4BCCC] rounded-full group-hover:border-[#4B4DFF] peer-checked:border-[4px] peer-checked:border-[#4B4DFF] peer-checked:bg-[#fff] transition-colors duration-200 translate-y-[3px]">
              <span className="w-2 h-2 rounded-full bg-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200"></span>
            </span>
            <span className="ml-[10px] text-[14px] text-[#6C7687] translate-y-[1px]">미확인 논문</span>
          </label>
        </div>

        {err && <div className="mb-4 text-red-600 text-sm">에러: {String(err)}</div>}

        <div className="relative">
          {/* 로딩 오버레이 - 첫 로딩일 때만 표시 */}
          {isFirstLoadRef.current && loading && <ChartSkeletonLoader show={loading} />}

          {/* 리스트 */}
          {filteredRows.length > 0 ? (
            <div className="flex flex-col gap-4">
              {filteredRows.map((item) => {
                const recordId = item.record_id || item.resource_id || item.id;
                const isConfirmed = item.self_confirm === 'Y' || item.self_confirm === 'y' || item.self_confirm === true;
                const qnaStatus = Number(item?.qna_status) || 0;
                const qnaActiveUid = Number(item?.qna_active_content_id) || 0;
                const qnaLabel = qnaStatus === 1 ? '문의중' : qnaStatus === 2 ? '처리 완료' : '';
                const qnaBaseUrl = String(rmVars?.homeUrl || '').replace(/\/$/, '');
                const qnaHref = qnaActiveUid
                  ? `${qnaBaseUrl}/qna/?list_mod=member_list&pageid=1&mod=document&uid=${qnaActiveUid}`
                  : '';
                const qnaNewInquiryHref = `${qnaBaseUrl}/qna/?list_mod=member_list&pageid=1&mod=editor`;

                return (
                  <div key={recordId} className="bg-white border border-[#E4E8F1] rounded-xl overflow-hidden hover:shadow-[0px_4px_20px_rgba(0,0,0,0.1)] transition duration-300">
                    <PublicationCard item={item} showAltmetric={false} noBorder />
                    {!isConfirmed && (
                      <div className="flex flex-col lg:flex-row justify-center items-center gap-x-6 gap-y-[14px] px-8 py-5 bg-[#F3F5F9] border-t border-[#E4E8F1]">
                        <p className="text-[14px] lg:text-[15px] font-bold text-[#333] text-center lg:text-left">
                          내 논문이며, 공동연구자의 정보가 정확한가요?
                        </p>

                        {(qnaStatus === 1 || qnaStatus === 2) && (qnaActiveUid > 0 || qnaStatus === 2) && (
                          <div className="flex flex-wrap items-center gap-3">
                            <span
                              className={`inline-flex items-center h-[24px] px-2 rounded-full text-[12px] font-semibold ${qnaStatus === 2 ? 'bg-[#D1FAE5] text-[#065F46]' : 'bg-[#DBEAFE] text-[#1D4ED8]'
                                }`}
                            >
                              {qnaLabel}
                            </span>
                            {qnaActiveUid > 0 && (
                              <a
                                href={qnaHref}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[13px] font-bold text-[#215BD8] underline underline-offset-2 hover:text-[#1747A8]"
                              >
                                문의 바로가기
                              </a>
                            )}
                            {qnaStatus === 2 && (
                              <a
                                href={qnaNewInquiryHref}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[13px] font-bold text-[#215BD8] underline underline-offset-2 hover:text-[#1747A8]"
                              >
                                새롭게 문의하기
                              </a>
                            )}
                          </div>
                        )}
                        <div className="flex justify-center items-center gap-[10px]">
                          <button
                            onClick={() => handleConfirm(recordId)}
                            disabled={submitting}
                            className="w-[65px] lg:w-[68px] h-[32px] bg-[#4B4DFF] text-white text-[14px] font-bold cursor-pointer rounded-full hover:bg-[#3335FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            예
                          </button>
                          <button
                            onClick={() => handleReject(recordId)}
                            disabled={submitting}
                            className="w-[65px] lg:w-[68px] h-[32px] bg-[#4B4DFF] text-white text-[14px] font-bold cursor-pointer rounded-full hover:bg-[#3335FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            아니오
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            !loading && (
              <div className="text-center py-8 text-[#8D9AAF]">
                데이터가 없습니다.
              </div>
            )
          )}
        </div>
      </div>

      {/* 수정 요청 팝업 */}
      {editModalOpen && (
        <>
          {/* 배경 오버레이 */}
          <div
            className="fixed inset-0 bg-black/50 z-[100]"
            onClick={() => !submitting && setEditModalOpen(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-[600px] w-[calc(100%-80px)] z-[101] bg-white rounded-xl p-6 shadow-[0px_4px_20px_0px_rgba(0,_0,_0,_0.1)]">
            <div className="flex justify-between items-center mb-6">
              <h4 className="text-[18px] font-bold">논문정보 수정 요청</h4>
              <button
                onClick={() => !submitting && setEditModalOpen(false)}
                className="cursor-pointer"
                disabled={submitting}
              >
                <img
                  src={`${rmVars.themeUrl}images/clear-icon-333.png`}
                  className="w-3"
                  alt="close"
                />
              </button>
            </div>

            <div className="flex flex-col gap-4 mb-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="editCategory"
                  checked={editCategory === '내 논문이 아닙니다.'}
                  onChange={() => setEditCategory('내 논문이 아닙니다.')}
                  className="w-4 h-4"
                />
                <span className="text-[14px]">내 논문이 아닙니다.</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="editCategory"
                  checked={editCategory === '저자 정보가 틀렸습니다.'}
                  onChange={() => setEditCategory('저자 정보가 틀렸습니다.')}
                  className="w-4 h-4"
                />
                <span className="text-[14px]">저자 정보가 틀렸습니다.</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="editCategory"
                  checked={editCategory === '학과 정보가 틀렸습니다.'}
                  onChange={() => setEditCategory('학과 정보가 틀렸습니다.')}
                  className="w-4 h-4"
                />
                <span className="text-[14px]">학과 정보가 틀렸습니다.</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="editCategory"
                  checked={editCategory === '기타'}
                  onChange={() => setEditCategory('기타')}
                  className="w-4 h-4"
                />
                <span className="text-[14px]">기타</span>
              </label>
            </div>

            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder="문의 내용을 작성해주세요."
              className="w-full h-[120px] px-4 py-3 border border-[#E4E8F1] rounded-lg text-[14px] outline-none resize-none mb-6"
              disabled={submitting}
            />

            <div className="flex justify-center gap-3">
              <button
                onClick={() => !submitting && setEditModalOpen(false)}
                disabled={submitting}
                className="px-6 py-2 border border-[#E4E8F1] rounded-lg text-[14px] font-medium text-[#6C7687] bg-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                취소
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={submitting}
                className="px-6 py-2 bg-[#4B4DFF] rounded-lg text-[14px] font-medium text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                저장
              </button>
            </div>
          </div>
        </>
      )}

      {/* 성공 팝업 */}
      {successModalOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[100]"
            onClick={() => setSuccessModalOpen(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-[400px] w-[calc(100%-80px)] z-[101] bg-white rounded-xl p-6 shadow-[0px_4px_20px_0px_rgba(0,_0,_0,_0.1)]">
            <div className="text-center">
              <p className="text-[16px] font-semibold mb-4">{successMessage}</p>
              <button
                onClick={() => setSuccessModalOpen(false)}
                className="px-6 py-2 bg-[#4B4DFF] rounded-lg text-[14px] font-medium text-white cursor-pointer"
              >
                확인
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
